import {
  getAssistantToolCatalogDefinition,
  getAssistantToolCatalogDefinitions,
} from '@/features/assistant/tool-catalog'
import { normalizeAssistantToolError } from '@/features/assistant/tool-errors'
import {
  serializeAssistantToolDefinitionToMcp,
  serializeAssistantToolErrorToMcp,
  serializeAssistantToolResultToMcp,
  type McpToolDescriptor,
  type McpToolResultEnvelope,
} from './mcp-serialization'
import { deserializeAssistantTransportValue } from '@/features/assistant/transport-value'
import {
  createAssistantToolContext,
  type AssistantToolSamplingClient,
} from '@/features/assistant/tool-context'

export type McpToolExecutor = (
  name: string,
  input?: Record<string, unknown>,
) => Promise<unknown>

export interface InvokeMcpToolOptions {
  samplingClient?: AssistantToolSamplingClient
}

async function executeAssistantToolThroughRegistry(
  name: string,
  input?: Record<string, unknown>,
  options: InvokeMcpToolOptions = {},
): Promise<unknown> {
  const { executeAssistantTool } = await import('@/features/assistant/tool-registry')
  return executeAssistantTool(
    name,
    input ?? {},
    createAssistantToolContext({
      invocationSource: 'mcp',
      samplingClient: options.samplingClient,
    }),
  )
}

export function getMcpToolCatalog(): McpToolDescriptor[] {
  return getAssistantToolCatalogDefinitions().map(serializeAssistantToolDefinitionToMcp)
}

export function getMcpToolDescriptor(name: string): McpToolDescriptor | null {
  const definition = getAssistantToolCatalogDefinition(name)
  return definition ? serializeAssistantToolDefinitionToMcp(definition) : null
}

export async function invokeMcpTool(
  name: string,
  input?: Record<string, unknown>,
  executor: McpToolExecutor = (toolName, toolInput) =>
    executeAssistantToolThroughRegistry(toolName, toolInput),
  options: InvokeMcpToolOptions = {},
): Promise<McpToolResultEnvelope> {
  try {
    const transportInput = deserializeAssistantTransportValue(input ?? {}) as Record<string, unknown>
    const result = await (
      executor === executeAssistantToolThroughRegistry
        ? executeAssistantToolThroughRegistry(name, transportInput, options)
        : executor(name, transportInput)
    )
    return await serializeAssistantToolResultToMcp(result)
  } catch (error) {
    return serializeAssistantToolErrorToMcp(normalizeAssistantToolError(error).toJSON())
  }
}
